import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Box, Button, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';

import { CissTextField } from 'components/fields';
import { ContentModal } from 'components/modal';
import { SubTitle } from 'components/text';
import { useMutateSellerVtexStatus } from 'pages/logistica/sellers-vetex/hooks';
import { SellerVtex } from 'pages/logistica/sellers-vetex/types';

type SellersVtexStatusModal = {
    open: boolean;
    seller: SellerVtex | null;
    setOpen: Dispatch<SetStateAction<SellerVtex | null>>;
};

export function SellersVtexStatusModal({ open, seller, setOpen }: SellersVtexStatusModal) {
    const [fgAtivo, setFgAtivo] = useState<boolean>(true);
    const [dsObservacao, setDsObservacao] = useState<string>('');
    const [touched, setTouched] = useState<boolean>(false);
    const { mutateSellersVtex, isLoading } = useMutateSellerVtexStatus({ setOpen });

    useEffect(() => {
        if (seller) {
            setFgAtivo(Boolean(seller.fgAtivo));
            setDsObservacao('');
            setTouched(false);
        }
    }, [seller, open]);

    const isConfirmDisabled = (fgAtivo === seller?.fgAtivo && !dsObservacao.trim()) || (!fgAtivo && !dsObservacao.trim());

    const handleConfirm = () => {
        if (!seller || isConfirmDisabled) return;

        mutateSellersVtex({
            fgAtivo,
            idSeller: seller.idSeller,
            dsObservacao: dsObservacao.trim(),
        });
    };

    return (
        <ContentModal
            closeButton
            open={open}
            setOpen={() => setOpen(null)}
            sx={{
                borderRadius: 2,
                minWidth: { xs: '90%', sm: 500 },
            }}
        >
            <SubTitle label="Alterar status do seller" />

            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography
                    sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        color: 'primary.main',
                        marginBottom: -1,
                    }}
                >
                    {seller?.dsNome}
                </Typography>

                <RadioGroup row value={fgAtivo ? 'ativo' : 'inativo'} onChange={(e) => setFgAtivo(e.target.value === 'ativo')}>
                    <FormControlLabel value="ativo" control={<Radio />} label="Ativo" />

                    <FormControlLabel value="inativo" control={<Radio />} label="Inativo" />
                </RadioGroup>

                <CissTextField
                    label="Motivo"
                    multiline
                    rows={3}
                    value={dsObservacao}
                    onChange={(e) => setDsObservacao(e.target.value)}
                    onBlur={() => setTouched(true)}
                    required={!fgAtivo}
                    error={touched && !fgAtivo && !dsObservacao.trim()}
                    helperText={touched && !fgAtivo && !dsObservacao.trim() ? 'O motivo é obrigatório ao inativar.' : ''}
                    placeholder="Informe o motivo da alteração..."
                    fullWidth
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                    <Button variant="outlined" onClick={() => setOpen(null)} disabled={isLoading}>
                        Cancelar
                    </Button>

                    <Button variant="contained" onClick={handleConfirm} disabled={isConfirmDisabled} loading={isLoading}>
                        Confirmar
                    </Button>
                </Box>
            </Box>
        </ContentModal>
    );
}
